import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { styled, keyframes } from "../../../styles";
import { ENVIRONMENT_BANNER_HEIGHT } from "../AdminLayout/AdminLayout.styles";

export const slideDown = keyframes({
  from: { height: 0 },
  to: { height: "var(--radix-accordion-content-height)" },
});

export const slideUp = keyframes({
  from: { height: "var(--radix-accordion-content-height)" },
  to: { height: 0 },
});

export const Container = styled("aside", {
  zIndex: 1101,
  backgroundColor: "$white",
  paddingTop: "18px",
  paddingLeft: "8px",
  paddingRight: "8px",
  width: "280px",
  borderRight: "1px solid $gray-40",
  position: "fixed",
  top: 62,
  // Top + bottom size it, so a long menu scrolls within the space left
  // between the top panel and the environment bar rather than running behind
  // the bar and hiding its last entries.
  bottom: ENVIRONMENT_BANNER_HEIGHT,
  overflow: "hidden",
  overflowY: "auto",
  transition: "width 250ms ease, padding 250ms ease",

  // Hide scrollbar but keep functionality
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },

  "@media screen and (max-width: 1100px)": {
    width: 0,
    // `auto` so top + bottom keep governing: with a height as well, the three
    // are over-constrained and `bottom` is the one the browser drops, which
    // would run the drawer behind the environment bar.
    height: "auto",
    border: 0,
    paddingTop: "66px",
    paddingLeft: "0px",
    paddingRight: "0px",
    transition: "ease 500ms",
  },

  variants: {
    mobileMenuOpen: {
      true: {
        "@media screen and (max-width: 1100px)": {
          width: "100%",
          paddingLeft: "16px",
          paddingRight: "16px",
          "&:hover": {
            width: "100%",
          },
        },
      },
    },
    collapsed: {
      true: {
        width: "56px",
        paddingLeft: "4px",
        paddingRight: "4px",
      },
    },
    expanded: {
      true: {
        width: "280px",
      },
    },
  },
});

export const SidebarHeader = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "0 4px 12px 4px",

  variants: {
    collapsed: {
      true: {
        justifyContent: "center",
        padding: "0 0 12px 0",
      },
    },
  },
});

export const CollapseToggleButton = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  width: "32px",
  height: "32px",
  borderRadius: "$3",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "$gray-90",
  transition: "background-color 0.2s ease",

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  "&:focus-visible": {
    outline: "2px solid var(--colors-navbar-link-focus-border)",
    outlineOffset: "2px",
  },

  "& svg": {
    width: "14px",
    height: "14px",
    transition: "transform 250ms ease",
  },

  variants: {
    collapsed: {
      true: {
        "& svg": {
          transform: "rotate(-90deg)",
        },
      },
      false: {
        "& svg": {
          transform: "rotate(90deg)",
        },
      },
    },
  },
});

export const MenuList = styled("nav", {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

export const StyledAccordion = styled(AccordionPrimitive.Root, {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

export const StyledAccordionItem = styled(AccordionPrimitive.Item, {
  overflow: "hidden",
});

export const StyledAccordionHeader = styled(AccordionPrimitive.Header, {
  all: "unset",
  display: "flex",
});

export const StyledAccordionTrigger = styled(AccordionPrimitive.Trigger, {
  all: "unset",
  boxSizing: "border-box",
  width: "100%",
  padding: "10px 12px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  borderRadius: "$3",
  cursor: "pointer",
  fontFamily: "$openSans",
  // Same size and line height as the portal's own left menu
  // (`LeftPanelMenu`), so switching between the two doesn't shrink the text.
  fontSize: "$text-xl",
  lineHeight: "21px",
  fontWeight: 400,
  color: "$gray-90",
  backgroundColor: "transparent",
  border: "2px solid transparent",
  transition: "background-color 0.2s ease",

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  "&:focus-visible": {
    outline: "none",
    border: "2px solid var(--colors-navbar-link-focus-border)",
  },

  variants: {
    active: {
      true: {
        fontWeight: 600,
        color: "$gray-100",
        backgroundColor: "$gray-30",
        "& svg": {
          fill: "$gray-100",
        },
        "& svg path:not([fill='none'])": {
          fill: "$gray-100",
        },
      },
    },
  },
});

export const StyledAccordionContent = styled(AccordionPrimitive.Content, {
  overflow: "hidden",
  paddingLeft: "20px",

  '&[data-state="open"]': {
    animation: `${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
  },
  '&[data-state="closed"]': {
    animation: `${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
  },
});

export const MenuItemLink = styled("a", {
  all: "unset",
  boxSizing: "border-box",
  width: "100%",
  padding: "10px 12px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  borderRadius: "$3",
  cursor: "pointer",
  fontFamily: "$openSans",
  fontSize: "$text-xl",
  lineHeight: "21px",
  fontWeight: 400,
  color: "$gray-90",
  backgroundColor: "transparent",
  border: "2px solid transparent",
  transition: "background-color 0.2s ease",
  textDecoration: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  "&:focus-visible": {
    outline: "none",
    border: "2px solid var(--colors-navbar-link-focus-border)",
  },

  variants: {
    active: {
      true: {
        fontWeight: 600,
        color: "$gray-100",
        backgroundColor: "$gray-30",
        "& svg": {
          fill: "$gray-100",
        },
        "& svg path:not([fill='none'])": {
          fill: "$gray-100",
        },
      },
    },
    disabled: {
      true: {
        opacity: 0.5,
        cursor: "not-allowed",
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    },
  },
});

export const ChildMenuItemLink = styled(MenuItemLink, {
  padding: "8px 12px",
});

export const IconContainer = styled("div", {
  width: "22px",
  height: "22px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "& svg": {
    width: "22px",
    height: "22px",
    fill: "$gray-90",
  },

  // The icon set hardcodes a fill on most of its paths — #666666 on some,
  // #727272 on others, #212121 on the few borrowed from the workflow builder —
  // and a presentation attribute like that beats the `fill` set on the <svg>
  // above. So the menu rendered three different greys and the active state
  // below could not darken any of them. A CSS rule outranks the attribute;
  // paths deliberately left `fill="none"` are outlines and must keep it.
  "& svg path:not([fill='none'])": {
    fill: "$gray-90",
  },
});

export const MenuItemText = styled("span", {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
});

export const ChevronIcon = styled("span", {
  marginLeft: "auto",
  transition: "transform 300ms cubic-bezier(0.87, 0, 0.13, 1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,

  "[data-state=open] &": {
    transform: "rotate(180deg)",
  },

  "& svg": {
    width: "12px",
    height: "12px",
  },
});

export const ChildMenuList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  paddingTop: "4px",
  paddingBottom: "4px",
});
